'use client';

import React, { useState, createContext, useContext, useEffect, useCallback, useMemo } from 'react';
import { CommonModal } from '../../common/modal';
import { LoginModal } from './LoginModal';
import { EmailPasswordRegistrationModal } from '../local/EmailPasswordRegistrationModal';
import { ResetPasswordWithTokenModal } from '../local/ResetPasswordWithTokenModal';
import { storePreviousPath, setLoginModalRole } from '@/utils/auth/common/auth-storage';
import { useHasPartnerTag } from '@/hooks/useHasPartnerTag';

interface LoginModalsContextType {
  openLoginModal: () => void;
  /**
   * Opens the modal with a message already showing (e.g. <SsoCallbackHandler/> reporting a
   * failed exchange). Kept as a separate action, not an argument to openLoginModal(), so that
   * stays a zero-arg trigger for its ~12 other call sites. Cleared on close.
   */
  openLoginModalWithNotice: (notice: string) => void;
  openLoginModalWithPartnerRole: () => void;
  openRegistrationModal: () => void;
  openResetPasswordWithTokenModal: (token: string) => void;
  closeLoginModal: () => void;
}

const LoginModalsContext = createContext<LoginModalsContextType | null>(null);

const BODY_CLASS = 'login-modal-open';

/** Lifts every auth modal above LoginPromptModal; one shared definition instead of pasting the selectors into each modal. */
const AUTH_MODAL_Z = "[&>div]:!z-[60] [&_[data-slot='dialog-overlay']]:!z-[60] [&_[data-slot='dialog-content']]:!z-[60]";

function HideBannersWhenAuthModalOpen({ isActive }: { isActive: boolean }) {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (isActive) {
      document.body.classList.add(BODY_CLASS);
    } else {
      document.body.classList.remove(BODY_CLASS);
    }
    return () => document.body.classList.remove(BODY_CLASS);
  }, [isActive]);
  return null;
}

/** Requires the provider — throws if it's missing, which is a wiring bug. */
export const useLoginModals = () => {
  const context = useContext(LoginModalsContext);
  if (!context) {
    throw new Error('useLoginModals must be used within LoginModalsProvider');
  }
  return context;
};

/**
 * Same context but returns null instead of throwing when no provider is above it, for code
 * that must work with or without the modal shell. Avoids wrapping useLoginModals() in
 * try/catch, which would violate rules of hooks.
 */
export const useOptionalLoginModals = () => useContext(LoginModalsContext);

interface LoginModalsManagerProps {
  children?: React.ReactNode;
}

/**
 * Owns the real login/registration/reset modal state, exposed only through
 * useLoginModals()/useOptionalLoginModals() — no ref API duplicates the context.
 */
export function LoginModalsManager({ children }: LoginModalsManagerProps) {
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState(false);
  const [isResetPasswordWithTokenModalOpen, setIsResetPasswordWithTokenModalOpen] = useState(false);
  const [resetPasswordToken, setResetPasswordToken] = useState('');
  const [notice, setNotice] = useState<string | null>(null);

  // Reactive, server-verdict-backed check — not a mount-time localStorage read.
  const isPartner = useHasPartnerTag();

  // Stores the current page so the auth flow can redirect back once it completes; shared by
  // every "open a modal" action below. No usePathname() dependency, so it stays stable and
  // doesn't rebuild the context value on every navigation.
  const storeCurrentPath = useCallback(() => {
    storePreviousPath(window.location.pathname + (window.location.search || ''));
  }, []);

  const openLoginModal = useCallback(() => {
    storeCurrentPath();
    setNotice(null);
    setIsLoginModalOpen(true);
  }, [storeCurrentPath]);

  const openLoginModalWithNotice = useCallback((message: string) => {
    openLoginModal();
    setNotice(message);
  }, [openLoginModal]);

  const openLoginModalWithPartnerRole = useCallback(() => {
    if (isPartner === true) return; // Already a partner; nothing to log in as.
    setLoginModalRole('partner');
    openLoginModal();
  }, [isPartner, openLoginModal]);

  const openRegistrationModal = useCallback(() => {
    setIsRegistrationModalOpen(true);
  }, []);

  const openResetPasswordWithTokenModal = useCallback((token: string) => {
    storeCurrentPath();
    setResetPasswordToken(token);
    setIsResetPasswordWithTokenModalOpen(true);
  }, [storeCurrentPath]);

  const closeLoginModal = useCallback(() => {
    setIsLoginModalOpen(false);
    setNotice(null);
    setIsRegistrationModalOpen(false);
    setIsResetPasswordWithTokenModalOpen(false);
    setResetPasswordToken('');
  }, []);

  /** Radix reports open and close through the same onOpenChange; treat close as "dismiss the whole flow". */
  const dismissOr = useCallback(
    (setOpen: (open: boolean) => void) => (open: boolean) => {
      if (open) setOpen(true);
      else closeLoginModal();
    },
    [closeLoginModal],
  );

  // Local only — publishing this on the context would re-render every consumer on each modal open.
  const isAnyAuthModalOpen =
    isLoginModalOpen || isRegistrationModalOpen || isResetPasswordWithTokenModalOpen;

  const contextValue: LoginModalsContextType = useMemo(() => ({
    openLoginModal,
    openLoginModalWithNotice,
    openLoginModalWithPartnerRole,
    openRegistrationModal,
    openResetPasswordWithTokenModal,
    closeLoginModal,
  }), [openLoginModal, openLoginModalWithNotice, openLoginModalWithPartnerRole, openRegistrationModal, openResetPasswordWithTokenModal, closeLoginModal]);

  return (
    <LoginModalsContext.Provider value={contextValue}>
      <HideBannersWhenAuthModalOpen isActive={isAnyAuthModalOpen} />
      {/* Login Modal - Higher z-index to appear above LoginPromptModal */}
      <CommonModal
        open={isLoginModalOpen && !isRegistrationModalOpen}
        onOpenChange={setIsLoginModalOpen}
        showCloseButton={false}
        className={AUTH_MODAL_Z}
      >
        <LoginModal
          isOpen={isLoginModalOpen && !isRegistrationModalOpen}
          onClose={closeLoginModal}
          onRegisterClick={() => {
            setIsRegistrationModalOpen(true);
          }}
          notice={notice}
        />
      </CommonModal>

      {/* Registration Modal - Direct Email/Password Registration - Higher z-index */}
      <CommonModal
        open={isRegistrationModalOpen}
        onOpenChange={dismissOr(setIsRegistrationModalOpen)}
        showCloseButton={false}
        className={`sm:max-w-[550px] md:max-w-[700px] lg:max-w-[960px] ${AUTH_MODAL_Z}`}
      >
        <EmailPasswordRegistrationModal
          onClose={closeLoginModal}
          onBack={() => setIsRegistrationModalOpen(false)}
        />
      </CommonModal>

      {/* Reset Password With Token Modal - Higher z-index */}
      <CommonModal
        open={isResetPasswordWithTokenModalOpen}
        onOpenChange={dismissOr(setIsResetPasswordWithTokenModalOpen)}
        showCloseButton={false}
        className={AUTH_MODAL_Z}
      >
        <ResetPasswordWithTokenModal onClose={closeLoginModal} token={resetPasswordToken} />
      </CommonModal>

      {children}
    </LoginModalsContext.Provider>
  );
}

